// @file Composable that calculates how to size the whiteboard canvas
import { isAppUsing } from '@@/bits/flip'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfaceWindowMobileNotchStore } from '@@/pinia/surface_window_mobile_notch'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardModeStore } from '@@/pinia/whiteboard_mode'
import { useWhiteboardPanelsStore } from '@@/pinia/whiteboard_panels'
import { useWhiteboardToolbarStore } from '@@/pinia/whiteboard_toolbar'
import { createSharedComposable } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

export const CANVAS_TEMPLATE_PREVIEW_PERSPECTIVE = 1000
export const CANVAS_TEMPLATE_PREVIEW_ROTATE_Y_DEGREE = 7

/**
 * Calculates the original width needed for an element that will undergo a CSS perspective transform,
 * so that the transformed element's final rendered width matches the expected width.
 *
 * This solves for the inverse of the CSS transform:
 * transform: perspective(${CANVAS_TEMPLATE_PREVIEW_PERSPECTIVE}px)
 *           rotateY(${CANVAS_TEMPLATE_PREVIEW_ROTATE_Y_DEGREE}deg);
 * transform-origin: center right;
 *
 * The calculation accounts for:
 * 1. Perspective projection (distance from viewpoint affects apparent size)
 * 2. Y-axis rotation (cosine projection)
 * 3. Perspective scaling based on z-depth
 *
 * The math solves the quadratic equation derived from:
 * expectedWidth = w * cos(θ) * (p / (p - w * sin(θ)))
 * where:
 * - w is the original width we're solving for
 * - p is the perspective distance
 * - θ is the rotation angle in radians
 *
 * When rearranged into standard form:
 * w * sin(θ) * cos(θ) * w + p * cos(θ) * w - p * expectedWidth = 0
 *
 * Solved using quadratic formula: (-b + √(b² - 4ac)) / (2a)
 * where:
 * a = sin(θ) * cos(θ)
 * b = p * cos(θ)
 * c = -p * expectedWidth
 *
 * @see Description of the math problem: https://github.com/padlet/mozart/pull/33623/files#r1917759643
 *
 * @param expectedWidth - The desired final width after the transform is applied (in pixels)
 * @returns The width the element should be set to before transform (in pixels)
 */
function calculateWidthBeforePerspectiveTransform(expectedWidth: number): number {
  const theta = CANVAS_TEMPLATE_PREVIEW_ROTATE_Y_DEGREE * (Math.PI / 180)
  const p = CANVAS_TEMPLATE_PREVIEW_PERSPECTIVE
  const a = Math.sin(theta) * Math.cos(theta)
  const b = p * Math.cos(theta)
  const c = -p * expectedWidth
  return (-b + Math.sqrt(b * b - 4 * a * c)) / (2 * a)
}

export const useWhiteboardCanvasSize = createSharedComposable(() => {
  const { surfaceContainerWidth, surfaceContainerHeight, isLandscapePhone } =
    storeToRefs(useSurfaceContainerSizeStore())
  const { isLoadingPortraitMobileLayout, xWhiteboardHeader } = storeToRefs(useWhiteboardStore())
  const { isSingleColumnLayout } = storeToRefs(useWhiteboardToolbarStore())
  const whiteboardModeStore = useWhiteboardModeStore()
  const whiteboardPanelsStore = useWhiteboardPanelsStore()

  const whiteboardHeaderHeight = computed(() => {
    if (!xWhiteboardHeader.value) return 0
    return 52
  })

  const shouldTransformCanvasPerspectiveAngle = computed(() => {
    return !isLandscapePhone.value && !isLoadingPortraitMobileLayout.value && whiteboardPanelsStore.xCardTemplatesPanel
  })

  const canvasWidth = computed(() => {
    if (whiteboardModeStore.isFullWidthFramePreviewMode) {
      return surfaceContainerWidth.value
    }
    if (whiteboardModeStore.isCodeMode) {
      return Math.min(surfaceContainerWidth.value / 2 - 2 * 40, 422) // 40 is padding, 422 is max width (to fit 5 columns for frame bar)
    }
    if (isLoadingPortraitMobileLayout.value) {
      return surfaceContainerWidth.value - 16 * 2
    }
    const W_H_RATIO = 16 / 9
    const MARGIN_FROM_SIDEBARS = isAppUsing('removePlayMode') ? 20 : 24
    const FRAME_BAR_WIDTH = isAppUsing('removePlayMode') ? 72 : 100
    const TOOLBAR_WIDTH = isSingleColumnLayout.value ? (isAppUsing('removePlayMode') ? 56 : 44) : 92
    const PLAY_MODE_NAVIGATION_BAR_HEIGHT = 40
    const MODE_SWITCHER_HEIGHT_BOTTOM_MARGIN = isAppUsing('removePlayMode') ? 0 : 56

    const height =
      surfaceContainerHeight.value -
      whiteboardHeaderHeight.value -
      ((whiteboardModeStore.isPlayMode || whiteboardModeStore.isScreenshotMode) && !isLandscapePhone.value
        ? PLAY_MODE_NAVIGATION_BAR_HEIGHT + MODE_SWITCHER_HEIGHT_BOTTOM_MARGIN
        : 32) -
      useSurfaceWindowMobileNotchStore().safeAreaInsetBottom

    let width =
      surfaceContainerWidth.value -
      2 * MARGIN_FROM_SIDEBARS -
      FRAME_BAR_WIDTH -
      TOOLBAR_WIDTH -
      useSurfaceWindowMobileNotchStore().safeAreaInsetLeft -
      useSurfaceWindowMobileNotchStore().safeAreaInsetRight

    if (shouldTransformCanvasPerspectiveAngle.value) {
      const aspectRatioCompliantCanvasWidth = width / height > W_H_RATIO ? height * W_H_RATIO : width
      const errorFactor = 0.789 // Magic number that corrects for the formula used below ¯\_(ツ)_/¯
      width = calculateWidthBeforePerspectiveTransform(aspectRatioCompliantCanvasWidth * errorFactor)
    }

    if (whiteboardModeStore.isPlayMode || whiteboardModeStore.isScreenshotMode) {
      width = surfaceContainerWidth.value - 2 * 40 // 40 is padding
    }

    if (width / height > W_H_RATIO) {
      return height * W_H_RATIO
    }
    return width
  })

  return {
    canvasWidth,
    shouldTransformCanvasPerspectiveAngle,
  }
})
